import dynamic from "next/dynamic";
import { resolveAntiBotMeta } from "../constants/antiBot";
import {
  AvailableFiltersResponse,
  DynamicContentsData,
  PaginationMeta,
} from "../utils/cms";
import { getNodeDynamicData } from "../utils/node/getNodeDynamicData";
import { readMegaMenuData } from "../utils/megaMenuContents";
import { NodeQueryParams } from "../utils/queryParams/nodeQueryParams";
import { SiteMetadata } from "../utils/s3";
import { resolveSiteIdentity } from "../utils/siteIdentity";
import { headerSiteProps } from "../utils/headerSiteProps";
import { Brand } from "../utils/brands";
import { DynamicStructure } from "../utils/interpolate";
import type { PageEntry } from "../utils/pageEntry";
import { BlockAnimation, getAnimationAttributes } from "../sections/common/BlockAnimation";
import { BlockAnimationWrapper } from "../sections/common/BlockAnimation/BlockAnimationWrapper";

const BannerCarouselWrapper = dynamic(() => import("./BannerCarousel"));
const BreadcrumbWrapper = dynamic(() => import("./Breadcrumb"));
const ContentWithBackgroundWrapper = dynamic(() => import("./ContentWithBackground"));
const DynamicFormBlockWrapper = dynamic(() => import("./DynamicForm"));
const FooterWrapper = dynamic(() => import("./Footer"));
const HeaderWrapper = dynamic(() => import("./Header"));
const HeaderV2Wrapper = dynamic(() => import("./HeaderV2"));
const ContentWithTabsWrapper = dynamic(() => import("./ContentWithTabs"));
const ComparativeTablesWrapper = dynamic(() => import("./ComparativeTables"));
const GalleryWrapper = dynamic(() => import("./Gallery"));
const MediaSectionWrapper = dynamic(() => import("./MediaSection"));
const ImageCenterCardsWrapper = dynamic(() => import("./ImageCenterCards"));
const ModelCarouselWrapper = dynamic(() => import("./ModelCarousel"));
const GalleryWithTabsWrapper = dynamic(() => import("./GalleryWithTabs"));
const HeroWrapper = dynamic(() => import("./Hero"));
const CodeBlockWrapper = dynamic(() => import("./CodeBlock"));
const CodeBlockDynamicWrapper = dynamic(() => import("./CodeBlockDynamic"));
const IconCardResourceWrapper = dynamic(() => import("./IconCardResource"));
const IconCardsWrapper = dynamic(() => import("./IconCards"));
const InstitutionalCardsWrapper = dynamic(() => import("./InstitutionalCards"));
const StaticCardsWrapper = dynamic(() => import("./StaticCards"));
const ModelColorPickerWrapper = dynamic(() => import("./ModelColorPicker"));
const ResourceWithIconWrapper = dynamic(() => import("./ResourceWithIcon"));
const ResourceWithIconCenterWrapper = dynamic(() => import("./ResourceWithIconCenter"));
const ResourceWithImageWrapper = dynamic(() => import("./ResourceWithImage"));
const TestimonialsWrapper = dynamic(() => import("./Testimonials"));
const FormOptionalFieldsWrapper = dynamic(() => import("./FormOptionalFields"));
const FormContentImageWrapper = dynamic(() => import("./FormContentImage"));
const RichTextWrapper = dynamic(() => import("./RichText"));
const GalleryV2Wrapper = dynamic(() => import("./GalleryV2"));
const MediaSectionDynamicWrapper = dynamic(() => import("./MediaSectionDynamic"));
const RichTextDynamic = dynamic(() => import("./RichTextDynamic"));
const DynamicCarouselAndGrid = dynamic(() => import("./DynamicCarouselAndGrid"));
const SpecificationsDynamic = dynamic(() => import("./SpecificationsDynamic"));
const VehicleItemsDynamic = dynamic(() => import("./VehicleItemsDynamic"));
const DynamicHighlightsWrapper = dynamic(() => import("./DynamicHighlights"));
const FaqWrapper = dynamic(() => import("./Faq"));
const DynamicListWrapper = dynamic(() => import("./DynamicList"));
const DynamicGalleryWrapper = dynamic(() => import("./DynamicGallery"));
const DynamicBannerWrapper = dynamic(() => import("./DynamicBanner"));
const DynamicFormWithGalleryWrapper = dynamic(() => import("./DynamicFormWithGallery"));
const DynamicCarouselWrapper = dynamic(() => import("./DynamicCarousel"));
const Viewer360Wrapper = dynamic(() => import("./Viewer360"));
const VersionColorSelectorWrapper = dynamic(() => import("./VersionColorSelector"));
const SimuladorFinanciamentoWrapper = dynamic(() => import("./SimuladorFinanciamento"));
const AgendamentoTestDriveWrapper = dynamic(() => import("./AgendamentoTestDrive"));
const UnitInfoWrapper = dynamic(() => import("./UnitInfo"));
const UnitCardsWrapper = dynamic(() => import("./UnitCards"));
const MediaSectionV2Wrapper = dynamic(() => import("./MediaSectionV2"));
const BreadcrumbDynamicWrapper = dynamic(() => import("./BreadcrumbDynamic"));
const SubscriptionCardsWrapper = dynamic(() => import("./SubscriptionCards"));
const SubscriptionOfferWrapper = dynamic(() => import("./SubscriptionOffer"));
const ViewCounterWrapper = dynamic(() => import("./ViewCounter"));
const SubscriptionOfferCardsWrapper = dynamic(() => import("./SubscriptionOfferCards"));
const SubscriptionListingWrapper = dynamic(() => import("./SubscriptionListing"));
const SubscriptionOfferV2Wrapper = dynamic(() => import("./SubscriptionOfferV2"));
const SubscriptionPageHeaderWrapper = dynamic(() => import("./SubscriptionPageHeader"));
const SubscriptionBannerWrapper = dynamic(() => import("./SubscriptionBanner"));
const SubscriptionHelpCenterWrapper = dynamic(() => import("./SubscriptionHelpCenter"));
const SubscriptionBenefitsWrapper = dynamic(() => import("./SubscriptionBenefits"));
const SubscriptionDocumentsWrapper = dynamic(() => import("./SubscriptionDocuments"));
const SubscriptionLegalTextWrapper = dynamic(() => import("./SubscriptionLegalText"));
const SubscriptionFaqWrapper = dynamic(() => import("./SubscriptionFaq"));
const SubscriptionTestimonialsWrapper = dynamic(() => import("./SubscriptionTestimonials"));
const SubscriptionStepsWrapper = dynamic(() => import("./SubscriptionSteps"));
const SubscriptionCompareWrapper = dynamic(() => import("./SubscriptionCompare"));
const SubscriptionFooterWrapper = dynamic(() => import("./SubscriptionFooter"));

interface NodeProps {
  node: any;
  data: any;
  dynamicPageContent?: any;
  dynamicContentsData?: DynamicContentsData;
  channelData?: any;
  nodeId?: string;
  formsData?: Record<string, any>;
  metadata?: SiteMetadata | null;
  brands?: Brand[];
  pageEntry?: PageEntry | null;
}

export const Node = ({
  node,
  data,
  dynamicPageContent,
  dynamicContentsData,
  channelData,
  nodeId,
  formsData,
  metadata,
  brands,
  pageEntry,
}: NodeProps) => {
  let typeName = "";
  if (typeof node.type === "object") {
    typeName = node.type.resolvedName;
  } else {
    typeName = node.type;
  }

  // Atribui valores dinâmicos do nó
  const nodeDynamicData = getNodeDynamicData<DynamicStructure["section"]>({
    nodeId,
    data: dynamicContentsData,
  });

  const nodeDynamicPagination = getNodeDynamicData<PaginationMeta>({
    nodeId,
    data: dynamicContentsData,
    suffix: "pagination",
  });

  const nodeDynamicAvailableFilters =
    getNodeDynamicData<AvailableFiltersResponse>({
      nodeId,
      data: dynamicContentsData,
      suffix: "availableFilters",
    });

  const nodeDynamicQueryParams = getNodeDynamicData<NodeQueryParams | null>({
    nodeId,
    data: dynamicContentsData,
    suffix: "nodeQueryParams",
  });

  const nodedynamicContentAvailableSorts = getNodeDynamicData({
    nodeId,
    data: dynamicContentsData,
    suffix: "availableSorts",
  });

  const nodeDynamicSearch = getNodeDynamicData({
    nodeId,
    data: dynamicContentsData,
    suffix: "search",
  });

  const nodeDynamicDefaultOrder = getNodeDynamicData({
    nodeId,
    data: dynamicContentsData,
    suffix: "defaultOrder",
  });

  const Children = node.nodes.map((childNodeId: string) => {
    return (
      <Node
        key={childNodeId}
        node={data[childNodeId]}
        data={data}
        dynamicPageContent={dynamicPageContent}
        dynamicContentsData={dynamicContentsData}
        channelData={channelData}
        nodeId={childNodeId}
        formsData={formsData}
        metadata={metadata}
        brands={brands}
        pageEntry={pageEntry}
      />
    );
  });

  const dynamicStructure: DynamicStructure = {
    item: dynamicPageContent,
    channel: channelData,
    section: nodeDynamicData,
    brands,
  };

  const siteIdentity = resolveSiteIdentity({ metadata, channelData });

  const animation: BlockAnimation = {
    animationEnabled: node.props?.animationEnabled,
    animationEffect: node.props?.animationEffect,
    animationType: node.props?.animationType,
    animationSpeed: node.props?.animationSpeed,
    animationDevices: node.props?.animationDevices,
  };

  const blockElement = (() => {
  switch (typeName) {
    case "Canva":
      return Children;
    case "DynamicFormWrapper":
    case "FormV2Wrapper":
      const formData =
        node.props?.selectedFormId && formsData
          ? formsData[node.props.selectedFormId]
          : null;

      return (
        <DynamicFormBlockWrapper
          {...node.props}
          formData={formData}
          dynamicStructure={dynamicStructure}
          accountId={siteIdentity.accountId}
          siteId={siteIdentity.siteId}
          contentEntryId={pageEntry?.id ?? undefined}
          moduleId={data?.moduleId ?? undefined}
          formId={node.props?.selectedFormId ?? undefined}
          antiBot={resolveAntiBotMeta(metadata?.antiBot)}
        />
      );
    case "GalleryWrapper":
      return <GalleryWrapper {...node.props} />;
    case "MediaSectionWrapper":
      return <MediaSectionWrapper {...node.props} />;
    case "MediaSectionV2Wrapper":
      return <MediaSectionV2Wrapper {...node.props} />;
    case "ImageCenterCardsWrapper":
      return <ImageCenterCardsWrapper {...node.props} />;
    case "ModelCarouselWrapper":
      return <ModelCarouselWrapper {...node.props} />;
    case "BannerCarouselWrapper":
      return <BannerCarouselWrapper {...node.props} />;
    case "ComparativeTablesWrapper":
      return <ComparativeTablesWrapper {...node.props} />;
    case "ContentWithBackgroundWrapper":
      return <ContentWithBackgroundWrapper {...node.props} />;
    case "ContentWithTabsWrapper":
      return <ContentWithTabsWrapper {...node.props} />;
    case "FooterWrapper":
      return <FooterWrapper {...node.props} />;
    case "HeaderWrapper":
      return (
        <HeaderWrapper
          {...node.props}
          {...headerSiteProps({ metadata, siteIdentity, pageEntry })}
        />
      );
    case "HeaderV2Wrapper":
      return (
        <HeaderV2Wrapper
          {...node.props}
          megaMenuData={readMegaMenuData({ nodeId, links: node.props?.links, dynamicContentsData })}
          dynamicStructure={dynamicStructure}
          {...headerSiteProps({ metadata, siteIdentity, pageEntry })}
        />
      );
    case "GalleryWithTabsWrapper":
      return <GalleryWithTabsWrapper {...node.props} />;
    case "HeroWrapper":
      return <HeroWrapper {...node.props} />;
    case "CodeBlockWrapper":
      return <CodeBlockWrapper {...node.props} />;
    case "IconCardResourceWrapper":
      return <IconCardResourceWrapper {...node.props} />;
    case "IconCardsWrapper":
      return <IconCardsWrapper {...node.props} />;
    case "InstitutionalCardsWrapper":
      return <InstitutionalCardsWrapper {...node.props} />;
    case "StaticCardsWrapper":
      return <StaticCardsWrapper {...node.props} />;
    case "ModelColorPickerWrapper":
      return <ModelColorPickerWrapper {...node.props} />;
    case "ResourceWithIconWrapper":
      return <ResourceWithIconWrapper {...node.props} />;
    case "ResourceWithIconCenterWrapper":
      return <ResourceWithIconCenterWrapper {...node.props} />;
    case "ResourceWithImageWrapper":
      return <ResourceWithImageWrapper {...node.props} />;
    case "TestimonialsWrapper":
      return (
        <TestimonialsWrapper
          {...node.props}
          dynamicStructure={dynamicStructure}
        />
      );
    case "FormContentImageWrapper":
      return (
        <FormContentImageWrapper
          {...node.props}
          antiBot={resolveAntiBotMeta(metadata?.antiBot)}
        />
      );
    case "FormOptionalFieldsWrapper":
      return (
        <FormOptionalFieldsWrapper
          {...node.props}
          accountId={siteIdentity.accountId}
          siteId={siteIdentity.siteId}
          contentEntryId={pageEntry?.id ?? undefined}
          channelUnits={channelData?.units || []}
          unitGroupingMode={channelData?.unitGroupingMode}
          unitGroups={channelData?.unitGroups}
          dynamicStructure={dynamicStructure}
          antiBot={resolveAntiBotMeta(metadata?.antiBot)}
        />
      );
    case "RichTextWrapper":
      return <RichTextWrapper {...node.props} />;
    case "GalleryV2Wrapper":
      return <GalleryV2Wrapper {...node.props} />;
    case "MediaSectionDynamicWrapper":
      return (
        <MediaSectionDynamicWrapper
          {...node.props}
          dynamicStructure={dynamicStructure}
        />
      );
    case "RichTextDynamicWrapper":
      return (
        <RichTextDynamic
          {...node.props}
          dynamicStructure={dynamicStructure}
        />
      );
    case "CodeBlockDynamicWrapper":
      return (
        <CodeBlockDynamicWrapper
          {...node.props}
          dynamicStructure={dynamicStructure}
        />
      );
    case "DynamicCarouselAndGridWrapper":
      return (
        <DynamicCarouselAndGrid
          {...node.props}
          dynamicStructure={dynamicStructure}
        />
      );
    case "SpecificationsDynamicWrapper":
      return (
        <SpecificationsDynamic
          {...node.props}
          dynamicStructure={dynamicStructure}
        />
      );
    case "DynamicHighlightsWrapper":
      return (
        <DynamicHighlightsWrapper
          {...node.props}
          dynamicStructure={dynamicStructure}
        />
      );
    case "VehicleItemsDynamicWrapper":
      return (
        <VehicleItemsDynamic
          {...node.props}
          dynamicStructure={dynamicStructure}
        />
      );
    case "FaqWrapper":
      return <FaqWrapper {...node.props} />;
    case "ViewCounterWrapper":
      return <ViewCounterWrapper {...node.props} />;
    case "BreadcrumbWrapper":
      return <BreadcrumbWrapper {...node.props} />;
    case "BreadcrumbDynamicWrapper":
      return (
        <BreadcrumbDynamicWrapper
          {...node.props}
          dynamicStructure={dynamicStructure}
        />
      );
    case "SubscriptionOfferCardsWrapper":
      return (
        <SubscriptionOfferCardsWrapper
          {...node.props}
          dynamicStructure={dynamicStructure}
          subscriptionBadges={channelData?.subscriptionConfig?.badges}
        />
      );
    case "SubscriptionListingWrapper":
      return (
        <SubscriptionListingWrapper
          {...node.props}
          nodeId={nodeId}
          dynamicStructure={dynamicStructure}
          dynamicContentPagination={nodeDynamicPagination}
          dynamicContentAvailableFilters={nodeDynamicAvailableFilters}
          dynamicNodeQueryParams={nodeDynamicQueryParams}
          dynamicContentAvailableSorts={nodedynamicContentAvailableSorts}
          dynamicContentSearch={nodeDynamicSearch}
          subscriptionBadges={channelData?.subscriptionConfig?.badges}
        />
      );
    case "SubscriptionHelpCenterWrapper":
      return <SubscriptionHelpCenterWrapper {...node.props} />;
    case "SubscriptionBenefitsWrapper":
      return <SubscriptionBenefitsWrapper {...node.props} />;
    case "SubscriptionDocumentsWrapper":
      return <SubscriptionDocumentsWrapper {...node.props} />;
    case "SubscriptionLegalTextWrapper":
      return <SubscriptionLegalTextWrapper {...node.props} />;
    case "SubscriptionFaqWrapper":
      return <SubscriptionFaqWrapper {...node.props} />;
    case "SubscriptionStepsWrapper":
      return <SubscriptionStepsWrapper {...node.props} />;
    case "SubscriptionFooterWrapper":
      return <SubscriptionFooterWrapper {...node.props} />;
    case "SubscriptionBannerWrapper":
      return <SubscriptionBannerWrapper {...node.props} />;
    case "SubscriptionPageHeaderWrapper":
      return <SubscriptionPageHeaderWrapper {...node.props} />;
    case "SubscriptionOfferV2Wrapper":
      return (
        <SubscriptionOfferV2Wrapper
          {...node.props}
          dynamicStructure={dynamicStructure}
          subscriptionBadges={channelData?.subscriptionConfig?.badges}
          subscriptionCoupons={channelData?.subscriptionConfig?.coupons}
          subscriptionOptionals={channelData?.subscriptionConfig?.optionals}
          channelUnits={channelData?.units}
          accountId={siteIdentity.accountId}
          siteId={siteIdentity.siteId}
          brandId={channelData?.brandPublicId ?? undefined}
          brandInternalId={channelData?.brandId ?? undefined}
          contentEntryId={pageEntry?.id ?? undefined}
          antiBot={resolveAntiBotMeta(metadata?.antiBot)}
        />
      );
    case "DynamicListWrapper":
      return (
        <DynamicListWrapper
          {...node.props}
          nodeId={nodeId}
          dynamicStructure={dynamicStructure}
          dynamicContentPagination={nodeDynamicPagination}
          dynamicContentAvailableFilters={nodeDynamicAvailableFilters}
          dynamicNodeQueryParams={nodeDynamicQueryParams}
          dynamicContentAvailableSorts={nodedynamicContentAvailableSorts}
          dynamicContentDefaultOrder={nodeDynamicDefaultOrder}
          dynamicContentSearch={nodeDynamicSearch}
          units={metadata?.units}
          brands={brands}
        />
      );
    case "SimuladorFinanciamentoWrapper":
      return (
        <SimuladorFinanciamentoWrapper
          {...node.props}
          dynamicStructure={dynamicStructure}
          accountId={siteIdentity.accountId}
          siteId={siteIdentity.siteId}
          antiBot={resolveAntiBotMeta(metadata?.antiBot)}
        />
      );
    case "AgendamentoTestDriveWrapper":
      return (
        <AgendamentoTestDriveWrapper
          {...node.props}
          dynamicStructure={dynamicStructure}
          accountId={siteIdentity.accountId}
          siteId={siteIdentity.siteId}
          moduleId={data?.moduleId ?? undefined}
          antiBot={resolveAntiBotMeta(metadata?.antiBot)}
        />
      );
    case "DynamicGalleryWrapper":
      return (
        <DynamicGalleryWrapper
          {...node.props}
          dynamicStructure={dynamicStructure}
        />
      );
    case "DynamicBannerWrapper":
      return (
        <DynamicBannerWrapper
          {...node.props}
          dynamicStructure={dynamicStructure}
        />
      );
    case "DynamicFormWithGalleryWrapper":
      const formDataForGallery =
        node.props?.selectedFormId && formsData
          ? formsData[node.props.selectedFormId]
          : null;

      return (
        <DynamicFormWithGalleryWrapper
          {...node.props}
          nodeId={nodeId}
          dynamicStructure={dynamicStructure}
          form={formDataForGallery}
          siteId={siteIdentity.siteId}
          accountId={siteIdentity.accountId}
          contentEntryId={pageEntry?.id ?? undefined}
          formId={node.props?.selectedFormId ?? undefined}
          antiBot={resolveAntiBotMeta(metadata?.antiBot)}
          moduleId={data?.moduleId ?? undefined}
        />
      );
    case "SubscriptionCardsWrapper":
      return (
        <SubscriptionCardsWrapper
          {...node.props}
          dynamicStructure={dynamicStructure}
          subscriptionBadges={channelData?.subscriptionConfig?.badges}
        />
      );
    case "SubscriptionTestimonialsWrapper":
      return (
        <SubscriptionTestimonialsWrapper
          {...node.props}
          dynamicStructure={dynamicStructure}
        />
      );
    case "SubscriptionCompareWrapper":
      return (
        <SubscriptionCompareWrapper
          {...node.props}
          dynamicStructure={dynamicStructure}
          subscriptionBadges={channelData?.subscriptionConfig?.badges}
        />
      );
    case "SubscriptionOfferWrapper":
      return (
        <SubscriptionOfferWrapper
          {...node.props}
          dynamicStructure={dynamicStructure}
          accountId={siteIdentity.accountId}
          siteId={siteIdentity.siteId}
          contentEntryId={pageEntry?.id ?? undefined}
          antiBot={resolveAntiBotMeta(metadata?.antiBot)}
        />
      );
    case "DynamicCarouselWrapper":
      return (
        <DynamicCarouselWrapper
          {...node.props}
          dynamicStructure={dynamicStructure}
        />
      );
    case "Viewer360Wrapper":
      return (
        <Viewer360Wrapper
          {...node.props}
          dynamicStructure={dynamicStructure}
        />
      );
    case "UnitInfoWrapper":
      return (
        <UnitInfoWrapper
          {...node.props}
          dynamicStructure={dynamicStructure}
        />
      );
    case "UnitCardsWrapper":
      return (
        <UnitCardsWrapper
          {...node.props}
          dynamicStructure={dynamicStructure}
        />
      );
    case "VersionColorSelectorWrapper":
      return (
        <VersionColorSelectorWrapper
          {...node.props}
          dynamicStructure={dynamicStructure}
        />
      );
    default:
      return null;
  }
  })();

  if (typeName === "Canva") return blockElement;

  const animationAttributes = getAnimationAttributes(animation);
  if (!animationAttributes) return blockElement;

  return (
    <BlockAnimationWrapper attributes={animationAttributes}>
      {blockElement}
    </BlockAnimationWrapper>
  );
};
